import { Suspense, lazy, useEffect, useMemo } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { App as AntdApp, ConfigProvider, Spin } from 'antd';
import { ThemeProvider } from '@admin-panel/config/theme';
import { AccessControlConfig, getResources } from '@admin-panel/access-control';
import { Refine, Authenticated } from '@refinedev/core';
import { ErrorComponent } from '@refinedev/antd';
import { AccessControlClient } from '@admin-panel/providers/lib/access/accesscontrolprovider';
import { LKAuthProvider } from '@admin-panel/providers/lib/authProvider';
import { restDataProvider } from '@admin-panel/providers/lib/dataprovider';
import routerProvider, { CatchAllNavigate } from '@refinedev/react-router';
import { GlobalProvider } from '@admin-panel/hooks';
import { FoundationLoader } from '@admin-panel/ui';
import { RiderAppShell } from './RiderAppShell';
import { initializeRiderPushNotifications } from './riderPushNotifications';
import './global.css';

const Logistics = lazy(() =>
  import('@admin-panel/pages/Logistics/Logistics').then(module => ({
    default: module.Logistics,
  })),
);
const MyRides = lazy(() =>
  import('@admin-panel/pages/Logistics/MyRides').then(module => ({ default: module.MyRides })),
);
const MyRideDetails = lazy(() =>
  import('@admin-panel/pages/Logistics/MyRideDetails').then(module => ({
    default: module.MyRideDetails,
  })),
);
const ReturnRideDetails = lazy(() =>
  import('@admin-panel/pages/Logistics/MyRideDetailsReturn').then(module => ({
    default: module.ReturnRideDetails,
  })),
);
const CashHandover = lazy(() =>
  import('@admin-panel/pages/Logistics/CashHandover').then(module => ({
    default: module.CashHandover,
  })),
);
const AndroidDownloadPage = lazy(() =>
  import('../pages/AndroidDownload').then(module => ({
    default: module.AndroidDownloadPage,
  })),
);
const Login = lazy(() =>
  import('@admin-panel/pages/auth/login').then(module => ({ default: module.Login })),
);
const WarehouseForgotPassword = lazy(() =>
  import('@admin-panel/pages/auth/WarehouseForgotPassword').then(module => ({
    default: module.default,
  })),
);
const WarehouseResetPassword = lazy(() =>
  import('@admin-panel/pages/auth/WarehouseResetPassword').then(module => ({
    default: module.default,
  })),
);

type RedirectWithSearchProps = {
  to: string;
};

const RedirectWithSearch = ({ to }: RedirectWithSearchProps) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search || ''}`} replace />;
};

const RouteFallback = () => (
  <div style={{ display: 'grid', minHeight: '50dvh', placeItems: 'center' }}>
    <Spin size="large" />
  </div>
);

export function App() {
  const resources = useMemo(() => getResources('LOGISTICS'), []);
  const accessControlClient = useMemo(() => new AccessControlClient(), []);

  useEffect(() => {
    initializeRiderPushNotifications();
  }, []);

  return (
    <ThemeProvider theme={{ token: { colorPrimary: '#393AFB', colorBgLayout: '#ffffff' } }}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#393AFB',
            colorBgLayout: '#ffffff',
            fontFamily: '"Hanken Grotesk"',
            colorText: '#171717',
          },
        }}
      >
        <AntdApp>
          <Refine
            authProvider={LKAuthProvider}
            accessControlProvider={accessControlClient.getAccessControlProvider()}
            dataProvider={restDataProvider}
            routerProvider={routerProvider}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              projectId: 'Unny6d-jT5kpU-QOXAra',
            }}
            resources={resources}
          >
            <AccessControlConfig resources={resources} dataprovider={accessControlClient}>
              <Routes>
                <Route
                  path="/android/staging/latest"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <AndroidDownloadPage track="staging" />
                    </Suspense>
                  }
                />
                <Route
                  path="/android/beta/debug/latest"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <AndroidDownloadPage track="beta-debug" />
                    </Suspense>
                  }
                />
                <Route
                  path="/android/beta/latest"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <AndroidDownloadPage track="beta" />
                    </Suspense>
                  }
                />
                <Route
                  path="/android/prod/latest"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <AndroidDownloadPage track="prod" />
                    </Suspense>
                  }
                />

                <Route
                  element={
                    <Authenticated
                      key="authenticated-routes"
                      fallback={<CatchAllNavigate to="/login" />}
                    >
                      <GlobalProvider>
                        <RiderAppShell>
                          <Suspense fallback={<FoundationLoader />}>
                            <Outlet />
                          </Suspense>
                        </RiderAppShell>
                      </GlobalProvider>
                    </Authenticated>
                  }
                >
                  <Route
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <Logistics />
                      </Suspense>
                    }
                  >
                    <Route index path="/" element={<RedirectWithSearch to="/rides" />} />
                    <Route
                      path="/rides"
                      element={
                        <Suspense fallback={<RouteFallback />}>
                          <MyRides />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/rides/:packageId"
                      element={
                        <Suspense fallback={<RouteFallback />}>
                          <MyRideDetails />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/returns/:returnId"
                      element={
                        <Suspense fallback={<RouteFallback />}>
                          <ReturnRideDetails />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/cash-handover"
                      element={
                        <Suspense fallback={<RouteFallback />}>
                          <CashHandover mode="rider" />
                        </Suspense>
                      }
                    />

                    <Route path="/logistics" element={<RedirectWithSearch to="/rides" />} />
                    <Route
                      path="/logistics/my-rides"
                      element={<RedirectWithSearch to="/rides" />}
                    />
                    <Route
                      path="/logistics/my-rides/:packageId"
                      element={
                        <Suspense fallback={<RouteFallback />}>
                          <MyRideDetails />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/logistics/my-rides/return/:returnId"
                      element={
                        <Suspense fallback={<RouteFallback />}>
                          <ReturnRideDetails />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/logistics/cash-handover"
                      element={<RedirectWithSearch to="/cash-handover" />}
                    />
                  </Route>
                </Route>

                <Route
                  path="/forgot-password"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <WarehouseForgotPassword consoleName="RIDERS" />
                    </Suspense>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <WarehouseResetPassword consoleName="RIDERS" />
                    </Suspense>
                  }
                />

                <Route
                  element={
                    <Authenticated key="auth-pages" fallback={<Outlet />}>
                      <RedirectWithSearch to="/rides" />
                    </Authenticated>
                  }
                >
                  <Route
                    path="/login"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <Login title="Login to Riders" />
                      </Suspense>
                    }
                  />
                </Route>

                <Route
                  element={
                    <Authenticated key="catch-all">
                      <RiderAppShell>
                        <Suspense fallback={<FoundationLoader />}>
                          <Outlet />
                        </Suspense>
                      </RiderAppShell>
                    </Authenticated>
                  }
                >
                  <Route path="*" element={<ErrorComponent />} />
                </Route>
              </Routes>
            </AccessControlConfig>
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </ThemeProvider>
  );
}

export default App;
